import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Home, Building2, Phone, MessageCircle, ArrowRight, MapPin } from "lucide-react";
import { navData } from "../../data/mockData";
import { requestQuote } from "../../lib/quoteIntent";

const PATHS = [
  {
    id: "domestic",
    icon: Home,
    title: "Domestic Cleaning Enquiries",
    text: "Regular weekly or fortnightly house cleaning, one-off cleans, deep cleans and end-of-tenancy cleaning for homes, flats and rentals.",
    service: "Regular Cleaning",
    links: [["Domestic cleaning", "/domestic-cleaning-cardiff"], ["Deep cleaning", "/deep-cleaning-cardiff"], ["End of tenancy", "/end-of-tenancy-cleaning-cardiff"]],
  },
  {
    id: "commercial",
    icon: Building2,
    title: "Commercial Cleaning Enquiries",
    text: "Office cleaning, workplace and shop cleaning, and contract cleaning on a schedule that works around your opening hours.",
    service: "Commercial Cleaning",
    links: [["Commercial cleaning", "/commercial-cleaning-cardiff"], ["Office cleaning", "/office-cleaning-cardiff"], ["Landlord contracts", "/landlord-letting-agent-cleaning-cardiff"]],
  },
];

const PathCard = ({ path }) => {
  const navigate = useNavigate();
  const Icon = path.icon;
  return (
    <div className="flex flex-col rounded-2xl border-2 border-sky-100 bg-white p-7 shadow-sm transition-shadow hover:shadow-lg" data-testid={`enquiry-path-${path.id}`}>
      <Icon className="mb-4 h-10 w-10 text-sky-600" />
      <h3 className="mb-2 text-xl font-bold text-slate-900">{path.title}</h3>
      <p className="mb-4 text-slate-600">{path.text}</p>
      <ul className="mb-6 flex flex-wrap gap-2">
        {path.links.map(([label, to]) => (
          <li key={to}><Link to={to} className="text-sm font-semibold text-sky-700 underline-offset-4 hover:underline">{label}</Link></li>
        ))}
      </ul>
      <div className="mt-auto flex flex-wrap gap-3">
        <button type="button" onClick={() => requestQuote(navigate, path.service)} className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-5 py-3 font-semibold text-white hover:bg-sky-700" data-testid={`enquiry-${path.id}-quote-btn`}>
          Get a Free Quote <ArrowRight className="h-4 w-4" />
        </button>
        <a href={`tel:${navData.phoneLink}`} className="inline-flex items-center gap-2 rounded-lg border-2 border-sky-600 px-5 py-3 font-semibold text-sky-700 hover:bg-sky-50" data-testid={`enquiry-${path.id}-call-btn`}>
          <Phone className="h-4 w-4" /> Call
        </a>
        <a href={navData.whatsappLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-green-600 px-5 py-3 font-semibold text-white hover:bg-green-700" data-testid={`enquiry-${path.id}-whatsapp-btn`}>
          <MessageCircle className="h-4 w-4" /> WhatsApp
        </a>
      </div>
    </div>
  );
};

const LocalIntro = () => (
  <div className="mt-14 grid gap-10 lg:grid-cols-[3fr_2fr]" data-testid="home-local-intro">
    <div className="space-y-4 text-slate-600">
      <h2 className="text-2xl font-bold text-slate-900">A local cleaning company in Cardiff for homes and businesses</h2>
      <p>
        Looking for cleaners near you? NeatWave Cleaning Services is a Cardiff cleaning company providing domestic cleaners for house cleaning and commercial cleaners for offices and workplaces. Our team is DBS-checked and fully insured, brings its own products and equipment, and follows a checklist on every visit.
      </p>
      <p>
        Homeowners book us for regular house cleaning in Cardiff, deep cleans and end-of-tenancy cleaning. Businesses use our commercial cleaning and office cleaning in Cardiff before or after working hours, so staff and customers arrive to a clean, professional space.
      </p>
    </div>
    <div className="rounded-2xl bg-sky-50 p-6">
      <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-slate-900"><MapPin className="h-5 w-5 text-sky-600" /> Areas we cover</h2>
      <p className="mb-4 text-sm text-slate-600">We travel to you from Cardiff. We don't have separate offices in Barry or Penarth.</p>
      <ul className="space-y-2 text-sm font-semibold">
        <li><a href="#services" className="text-sky-700 hover:underline">Cleaning services in Cardiff</a></li>
        <li><Link to="/cleaning-services-barry" className="text-sky-700 hover:underline" data-testid="home-area-barry">Cleaning services in Barry</Link></li>
        <li><Link to="/cleaning-services-penarth" className="text-sky-700 hover:underline" data-testid="home-area-penarth">Cleaning services in Penarth</Link></li>
      </ul>
    </div>
  </div>
);

const HomeIntroSection = () => (
  <section className="bg-white py-16" data-testid="home-intro-section">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <h2 className="mb-3 text-3xl font-bold text-slate-900 md:text-4xl">Cleaning services in Cardiff, Barry and Penarth</h2>
      <p className="mb-10 max-w-3xl text-lg text-slate-600">Tell us whether you need cleaning for your home or your business, and we'll take you straight to the right quote.</p>
      <div className="grid gap-6 md:grid-cols-2">
        {PATHS.map((p) => <PathCard key={p.id} path={p} />)}
      </div>
      <LocalIntro />
    </div>
  </section>
);

export default HomeIntroSection;
